import React from 'react';
import { Box, Typography, Container } from '@mui/material';

const TextBlock = ({
  heading = 'Section Heading',
  content = '<p>This is a paragraph of text. You can edit this using the rich text editor.</p>',
  backgroundColor = '#ffffff',
  textColor = '#333333',
  padding = '2rem',
  align = 'left',
  maxWidth = '800px'
}) => {
  console.log('Rendering TextBlock component with props:', { heading, content: content.substring(0, 50) + '...' });
  
  // Function to sanitize and prepare content
  const prepareContent = () => {
    // If content is already HTML, return it
    if (content.trim().startsWith('<')) {
      return content;
    }
    
    // If content contains newlines but isn't HTML, convert to paragraphs
    if (content.includes('\n')) {
      return content.split('\n').map(line => 
        line.trim() ? `<p>${line.trim()}</p>` : ''
      ).join('');
    }
    
    // Default case, wrap in paragraph if not empty
    return content.trim() ? `<p>${content}</p>` : '';
  };
  
  const preparedContent = prepareContent();
  
  return (
    <Box
      sx={{
        backgroundColor,
        color: textColor,
        padding,
        width: '100%'
      }}
    >
      <Container 
        sx={{ 
          maxWidth: maxWidth, 
          textAlign: align,
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }}
      >
        {heading && (
          <Typography variant="h4" component="h2" gutterBottom>
            {heading}
          </Typography>
        )}
        <Box 
          dangerouslySetInnerHTML={{ __html: preparedContent }} 
          sx={{ 
            '& p': { 
              marginBottom: '1rem',
              lineHeight: 1.6
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginTop: '1.5rem',
              marginBottom: '0.75rem'
            },
            '& ul, & ol': {
              marginBottom: '1rem',
              paddingLeft: '2rem'
            },
            '& li': {
              marginBottom: '0.5rem'
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto'
            },
            '& code': {
              backgroundColor: '#f5f5f5',
              padding: '0.2rem 0.4rem',
              borderRadius: '3px',
              fontFamily: 'monospace'
            },
            '& pre': {
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
              overflowX: 'auto',
              fontFamily: 'monospace'
            }
          }}
        />
      </Container>
    </Box>
  );
};

// Define the fields for the Puck editor
// Updated for Puck v0.18.2 compatibility with standard field types
TextBlock.fields = {
  heading: {
    type: 'text',
    label: 'Heading',
    default: 'Section Heading',
    description: 'The main heading for this text block'
  },
  content: {
    type: 'textarea',
    label: 'Content',
    default: '<p>This is a paragraph of text. You can edit this using the rich text editor.</p>',
    description: 'The main content of the text block. Supports HTML formatting.'
  },
  backgroundColor: {
    type: 'text',
    label: 'Background Color',
    default: '#ffffff',
    description: 'The background color of the text block'
  },
  textColor: {
    type: 'text',
    label: 'Text Color',
    default: '#333333',
    description: 'The color of the text in this block'
  },
  padding: {
    type: 'text',
    label: 'Padding',
    default: '2rem',
    description: 'The padding around the content (e.g., 2rem, 20px)'
  },
  align: {
    type: 'select',
    label: 'Text Alignment',
    options: [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' }
    ],
    default: 'left',
    description: 'The horizontal alignment of the text'
  },
  maxWidth: {
    type: 'text',
    label: 'Max Width',
    default: '800px',
    description: 'The maximum width of the content container'
  }
};

// Define the component's display properties for the editor
TextBlock.componentOptions = {
  // Component preview in the editor
  preview: () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: '#f5f5f5', 
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100px'
    }}>
      <Typography variant="subtitle1" fontWeight="bold">Text Block</Typography>
      <Typography variant="caption">Rich text content with customizable styling</Typography>
    </Box>
  ),
  // Category for component organization
  category: 'Content',
  // Icon for the component in the editor
  icon: '📝'
};

export default TextBlock; 